// ============================================================================
// Piezas visuales COMPARTIDAS de los widgets del dashboard (Inicio).
// "Cambiar uno = cambiar todos": los widgets actuales (Estadísticas del Día,
// Metas, Mis Estadísticas) y los que vengan (Récord, Pulso de Publicidad,
// etc.) arman su look con estas piezas — NUNCA recrearlas inline.
// Lenguaje aprobado por Toto jul 2026 (ver memoria widgets-dashboard-lenguaje).
// ============================================================================
import React from 'react';
import { estadoConfig } from '../ui/estadoPedido';
import { ArrowTrendingUpIcon } from '../ui/Icons';

// Superficie común de KPI tiles y cards de visualización.
// textAlign explícito: en Inicio los widgets viven dentro de .welcome-screen
// (text-align: center) y sin esto los tiles heredan el centrado.
export const ESTILO_TILE = {
  backgroundColor: 'var(--color-card-elevado)',
  border: '1px solid var(--color-borde-card)',
  borderRadius: 'var(--radio-lg)',
  padding: '14px 16px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  textAlign: 'left'
};

// Mini-línea de tendencia (SVG puro, sin librerías)
export function Sparkline({ valores, color }) {
  if (!valores || valores.length < 2) return null;
  const max = Math.max(...valores);
  const min = Math.min(...valores);
  const rango = max - min || 1;
  const puntos = valores
    .map((v, i) => {
      const x = (i / (valores.length - 1)) * 100;
      const y = 20 - ((v - min) / rango) * 16;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox="0 0 100 22" preserveAspectRatio="none" style={{ width: '100%', height: '22px', display: 'block', marginTop: '8px' }}>
      <polyline points={puntos} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// Badge de tendencia (▲ verde / ▼ rojo / ≈ gris). Por defecto compara contra
// ayer; `etiqueta`/`textoIgual`/`titulo` permiten otras comparaciones (ej.
// "vs necesario" en Metas) sin duplicar la pieza. `invertir` da vuelta los
// COLORES (no la flecha) para métricas donde subir es MALO (ej. tasa de
// devoluciones): sube → rojo, baja → verde.
export function BadgeTendencia({ pct, etiqueta = 'vs ayer', textoIgual = 'igual que ayer', titulo = 'Comparado contra el total de ayer', invertir = false }) {
  if (pct === null || pct === undefined || !isFinite(pct)) return null;
  const igual = Math.abs(pct) < 1;
  const sube = pct >= 1;
  const bueno = invertir ? !sube : sube;
  const fondo = igual ? 'var(--color-info-fondo)' : bueno ? 'var(--color-exito-fondo)' : 'var(--color-peligro-fondo)';
  const texto = igual ? 'var(--color-info-texto)' : bueno ? 'var(--color-exito-texto)' : 'var(--color-peligro-texto)';
  return (
    <span
      title={titulo}
      style={{
        fontSize: '11px',
        fontWeight: 'bold',
        backgroundColor: fondo,
        color: texto,
        padding: '2px 6px',
        borderRadius: 'var(--radio-sm)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        whiteSpace: 'nowrap'
      }}
    >
      {igual ? '≈' : (
        <span style={{ display: 'inline-flex', transform: sube ? 'none' : 'scaleY(-1)' }}>
          <ArrowTrendingUpIcon className="w-3 h-3" />
        </span>
      )}
      {igual ? textoIgual : `${sube ? '+' : ''}${pct.toFixed(0)}% ${etiqueta}`}
    </span>
  );
}

// Dona de estados de pedido + leyenda (estados con valor) + franja "En 0" abajo.
// `conteos` = objeto { ESTADO: cantidad }, `total` = número del centro.
// Va DENTRO de una card con display:flex column (la fila interna lleva flex:1).
const CIRCUNFERENCIA_DONA = 2 * Math.PI * 40; // r=40 en el viewBox de 100x100

export function DonaEstados({ conteos, total }) {
  const entradas = Object.entries(conteos || {});
  const conValor = entradas.filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]);
  const enCero = entradas.filter(([, c]) => !c);
  const totalDona = conValor.reduce((s, [, c]) => s + c, 0);
  let offset = 0;
  const segmentos = conValor.map(([estado, cantidad]) => {
    const largo = totalDona > 0 ? (cantidad / totalDona) * CIRCUNFERENCIA_DONA : 0;
    const seg = { estado, cantidad, largo, offset, color: estadoConfig(estado).color };
    offset += largo;
    return seg;
  });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
        <svg viewBox="0 0 100 100" style={{ width: '96px', height: '96px', flexShrink: 0 }}>
          <circle cx="50" cy="50" r="40" fill="none" stroke="var(--color-borde)" strokeWidth="12" />
          {segmentos.map((seg) => (
            <circle
              key={seg.estado}
              cx="50" cy="50" r="40" fill="none"
              stroke={seg.color}
              strokeWidth="12"
              strokeDasharray={`${seg.largo} ${CIRCUNFERENCIA_DONA}`}
              strokeDashoffset={-seg.offset}
              transform="rotate(-90 50 50)"
            />
          ))}
          <text x="50" y="47" textAnchor="middle" style={{ fontSize: '20px', fontWeight: 'bold' }} fill="var(--color-texto)">{total}</text>
          <text x="50" y="62" textAnchor="middle" style={{ fontSize: '9px' }} fill="var(--color-texto-secundario)">total</text>
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '12px', minWidth: 0 }}>
          {conValor.map(([estado, cantidad]) => {
            const cfg = estadoConfig(estado);
            const Icon = cfg.Icon;
            return (
              <span key={estado} style={{ color: cfg.color, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                {Icon && <Icon className="w-4 h-4" />} {cfg.label || estado}
                <strong style={{ color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>{cantidad}</strong>
              </span>
            );
          })}
        </div>
      </div>
      {enCero.length > 0 && (
        <div style={{
          marginTop: '10px',
          paddingTop: '8px',
          borderTop: '1px solid var(--color-borde-suave)',
          fontSize: '11px',
          color: 'var(--color-texto-secundario)',
          textAlign: 'left',
          lineHeight: 1.6
        }}>
          En 0: {enCero.map(([estado]) => estadoConfig(estado).label || estado).join(' · ')}
        </div>
      )}
    </>
  );
}
